(ns portal.adserver-ocp.db
  (:require [clojure.data :refer [diff]]
            [clojure.set :refer [rename-keys]]
            [clojure.spec.alpha :as spec]
            [clojure.string :as str]
            [common.db.interceptors :as interceptors]
            [common.form.core :as form]
            [common.queries :refer [use-query]]
            [common.resource.query.adserver :as adserver.q]
            [common.url :as url]
            [phrase.alpha :as p]
            [re-frame.core :as rf]
            [uix.core :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(def entity-name "Adserver")

(def initial-value
  {:name ""
   :adserver-id nil
   :api-access false})

(def adservers-with-raw-credentials #{1 5})

(defn adserver-raw-credentials?
  [adserver-id]
  (contains? adservers-with-raw-credentials adserver-id))

(defn api-method
  [adserver-id api-access]
  (cond
    (and (adserver-raw-credentials? adserver-id) (not api-access)) 0
    (and (adserver-raw-credentials? adserver-id) api-access) 1
    :else 2))

(spec/def ::name
  (spec/and string?
            (complement str/blank?)))

(spec/def ::adserver-id int?)

(spec/def ::username
  (spec/and
   string?
   (complement str/blank?)))

(spec/def ::password
  (spec/and
   string?
   (complement str/blank?)))

(defmulti field-spec
  (fn [{:keys [api-access]}]
    (if api-access
      :with-password
      :raw-credential)))

(defmethod field-spec :raw-credential [_]
  (spec/keys :req-un [::name ::adserver-id]))

(defmethod field-spec :with-password [_]
  (spec/keys :req-un [::name ::adserver-id ::username ::password]))

(spec/def ::value
  (spec/multi-spec field-spec :api-access))

(def validate-form
  (rf/enrich
   (form/get-validate-form-fn :form.adserver ::value)))

(def ui-messages {:create {:success "Successfully created adserver"
                           :failure "Failed to create adserver"}
                  :edit {:success "Successfully saved adserver"
                         :failure "Failed to save adserver"}
                  :delete {:success "Successfully deleted adserver"
                           :failure "Failed to delete adserver"}})

(def forward-events [{:events #{:resource.adserver-account/create-success}
                      :dispatch-to [:form.adserver/submit-success]}
                     {:events #{:resource.adserver-account/create-failure}
                      :dispatch-to [:form.adserver/submit-failure]}
                     {:events #{:resource.adserver-account/patch-success}
                      :dispatch-to [:form.adserver/submit-success]}
                     {:events #{:resource.adserver-account/patch-failure}
                      :dispatch-to [:form.adserver/submit-failure]}
                     {:events #{:resource.adserver-account/adform-oauth-success}
                      :dispatch-to [:form.adserver/oauth-success]}
                     {:events #{:resource.adserver-account/adform-oauth-failure}
                      :dispatch-to [:form.adserver/oauth-failure]}
                     {:events #{:resource.adserver-account/adform-verify-failure}
                      :dispatch-to [:form.adserver/oauth-failure]}
                     {:events #{:resource.adserver-account/adform-forget-success}
                      :dispatch-to [:form.adserver/forget-success]}
                     {:events #{:resource.adserver-account/adform-forget-failure}
                      :dispatch-to [:form.adserver/oauth-failure]}
                     {:events #{:resource.adserver-account/dcm-oauth-success}
                      :dispatch-to [:form.adserver/oauth-success]}
                     {:events #{:resource.adserver-account/dcm-oauth-failure}
                      :dispatch-to [:form.adserver/oauth-failure]}
                     {:events #{:resource.adserver-account/dcm-verify-failure}
                      :dispatch-to [:form.adserver/oauth-failure]}
                     {:events #{:resource.adserver-account/dcm-forget-success}
                      :dispatch-to [:form.adserver/forget-success]}
                     {:events #{:resource.adserver-account/dcm-forget-failure}
                      :dispatch-to [:form.adserver/oauth-failure]}
                     {:events #{:resource.adserver-account/userprofile-success}
                      :dispatch-to [:form.adserver/refresh-adserver-accounts]}
                     {:register :adserver-delete-success
                      :events #{:resource.adserver-account/delete-success}
                      :dispatch-to [:form.adserver/submit-success]}
                     {:register :adserver-delete-failure
                      :events #{:resource.adserver-account/delete-failure}
                      :dispatch-to [:form.adserver/submit-failure]}
                     {:register :adserver-get-one
                      :events #{:resource.adserver-account/get-one-success}
                      :dispatch-to [:form.adserver/get-one-success]}])

(form/register-form {:namespace :form.adserver
                     :initial-value initial-value
                     :validate-form validate-form
                     :forward-events forward-events
                     :submit-event :form.adserver/submit-form})

(defn adserver-values
  [adserver]
  (let [adserver-id (:id (:adserver adserver))
        username (:apiUsername adserver)
        profile-id (:dcmUserprofile adserver)
        api-access (and (pos? (:apiMethod adserver))
                        (adserver-raw-credentials? adserver-id))
        oauth-acccess (:hasOauth2Token adserver)]
    (hash-map :name (:name adserver)
              :api-access api-access
              :username username
              :profile-id profile-id
              :oauth-access oauth-acccess
              :adserver-id adserver-id)))

(rf/reg-event-fx :form.adserver/init-form
  (fn [{:keys [db]} [_ adserver]]
    (let [adserver* (adserver-values adserver)
          new-db (cond-> db
                   (:apiMethod adserver) (update-in [:form.adserver] assoc :api-method (:apiMethod adserver))
                   adserver (update-in [:form.adserver] assoc :value (merge initial-value adserver*) :initial-value adserver* :mode :edit)
                   (:id adserver) (-> (update-in [:form.adserver :value] assoc :id (:id adserver))
                                      (update-in [:form.adserver :initial-value] assoc :id (:id adserver)))
                   (not adserver) (assoc-in [:form.adserver :mode] :create)
                   true (dissoc :resource.adserver-account/dcm-verify))]
      {:db new-db
       :dispatch-n [[:form.adserver/init]
                    (when (:oauth-access adserver*) [:form.adserver/oauth-verify])]})))

(defn map-profile-options
  [profiles]
  (map #(hash-map :label (str (:profileId %) " - " (:accountName %))
                  :value (:profileId %)) profiles))

(defn form-data->api-method [{:keys [adserver-id api-access]}]
  (api-method adserver-id api-access))

(defn form-data->payload
  [{:keys [adserver-id name username password]} value api-method]
  (let [id (:id value)
        add-api-method? (and (some? api-method) (or (nil? id) (not= api-method 2)))]
    (cond-> {}
      id (assoc :id id)
      add-api-method? (assoc :apiMethod api-method)
      adserver-id (assoc :adserverId adserver-id)
      name (assoc :name name)
      (or (= api-method 1) username) (assoc :apiUsername (:username value))
      password (assoc :apiPassword password))))

(rf/reg-event-fx :form.adserver/submit-form
  [(rf/inject-cofx :sub :form.adserver/mode)
   (rf/inject-cofx :sub :form.adserver/submittable?)
   validate-form]
  (fn [{:keys [db]
        :form.adserver/keys [mode submittable?]} _]
    (let [{:keys [value initial-value]} (:form.adserver db)
          payload (form-data->payload (first (diff value initial-value))
                                      value
                                      (first (diff
                                              (form-data->api-method value)
                                              (get-in db [:form.adserver :api-method]))))
          event (if (= :edit mode)
                  :resource.adserver-account/patch
                  :resource.adserver-account/create)]
      {:dispatch-n [[:loader/open]
                    (if submittable?
                      [event payload]
                      [:form.adserver/submit-success])]})))

(rf/reg-event-fx :form.adserver/submit-success
  [(rf/inject-cofx :sub :form.adserver/mode)]
  (fn [{:form.adserver/keys [mode]} _]
    {:fx [[:dispatch [:ocp/close]]
          [:dispatch [:loader/close]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message (get-in ui-messages [mode :success])}]]
          [:dispatch [:form.adserver/refresh-adserver-accounts]]]}))

(rf/reg-event-fx :form.adserver/submit-failure
  [(rf/inject-cofx :sub :form.adserver/mode)
   interceptors/forwarded-event]
  (fn [{:keys [db]
        :form.adserver/keys [mode]}
       [event response]]
    (let [error-message (get-in response [:value :error :message])]
      {:db (update-in db [:form.adserver] assoc :backend-error error-message)
       :dispatch-n [[:loader/close]
                    [:response-handler/handle-response
                     {:entity-name entity-name
                      :type :error
                      :event event
                      :message (get-in ui-messages [mode :failure])
                      :response response}]]})))

(rf/reg-event-fx :form.adserver/oauth-success
  [interceptors/forwarded-event]
  (fn [_ [_ {:keys [url]}]]
    {:router/assign url}))

(rf/reg-event-fx :form.adserver/oauth-failure
  [interceptors/forwarded-event]
  (fn [{:keys [db]} [event response]]
    (let [error-message (get-in response [:value :error :message])]
      {:db (-> db
               (update-in [:form.adserver] assoc :backend-error error-message)
               (assoc-in [:form.adserver :value :oauth-access] false))
       :dispatch [:response-handler/handle-response
                  {:entity-name entity-name
                   :type :error
                   :event event
                   :response response}]})))

(rf/reg-event-fx :form.adserver/forget-success
  (fn [{:keys [db]} _]
    {:db (assoc-in db [:form.adserver :value :oauth-access] false)
     :fx [[:dispatch [:form.adserver/refresh-adserver-accounts]]]}))

(rf/reg-event-fx :form.adserver/refresh-adserver-accounts
  [(rf/inject-cofx :sub :url-context)]
  (fn [{:keys [url-context]} _]
    {:fx [[:invalidate-queries {:queryKey ["adserver-accounts" (:agency-id url-context)]}]
          [:invalidate-queries {:queryKey ["adserver-accounts-paginated" (:agency-id url-context)]}]]}))

(rf/reg-event-db :form.adserver/set-adserver
  [validate-form]
  (fn [db [_ _ adserver-id]]
    (-> db
        (assoc-in [:form.adserver :value :adserver-id] adserver-id)
        (assoc-in [:form.adserver :value :api-access] false))))

(rf/reg-event-fx :form.adserver/set-profile
  (fn [{:keys [db]} [_ _ profile-id]]
    (let [id (get-in db [:form.adserver :value :id])]
      {:db (assoc-in db [:form.adserver :value :profile-id] profile-id)
       :dispatch [:resource.adserver-account/userprofile {:userProfile profile-id} id]})))

(rf/reg-event-fx :form.adserver/toggle-api-access
  [(rf/inject-cofx :sub :form.adserver/api-access)
   validate-form]
  (fn [{:keys [db]
        :form.adserver/keys [api-access]}]
    (let [new-db (cond-> (assoc-in db [:form.adserver :value :api-access] (not api-access))
                   api-access (update-in [:form.adserver :value] dissoc :password)
                   api-access (assoc-in [:form.adserver :value :username] (get-in db [:form.adserver :initial-value :username])))]
      {:db new-db})))

(rf/reg-event-fx :form.adserver/delete
  (fn [{:keys [db]} [_ id]]
    {:db (assoc-in db [:form.adserver :mode] :delete)
     :dispatch [:resource.adserver-account/delete id]}))

(rf/reg-event-fx :form.adserver/oauth-authorize
  [(rf/inject-cofx :sub :form.adserver/current-adserver-type)]
  (fn [{:keys [db]
        :form.adserver/keys [current-adserver-type]}]
    (let [id (get-in db [:form.adserver :value :id])
          event (condp = current-adserver-type
                  :adform :resource.adserver-account/adform-oauth
                  :dcm :resource.adserver-account/dcm-oauth
                  nil)]
      {:dispatch [event id]})))

(rf/reg-event-fx :form.adserver/oauth-verify
  [(rf/inject-cofx :sub :form.adserver/current-adserver-type)]
  (fn [{:keys [db]
        :form.adserver/keys [current-adserver-type]}]
    (let [id (get-in db [:form.adserver :value :id])
          event (condp = current-adserver-type
                  :adform :resource.adserver-account/adform-verify
                  :dcm :resource.adserver-account/dcm-verify
                  nil)]
      {:dispatch-n [(when id [event id])]})))

(rf/reg-event-fx :form.adserver/oauth-forget
  [(rf/inject-cofx :sub :form.adserver/current-adserver-type)]
  (fn [{:keys [db]
        :form.adserver/keys [current-adserver-type]}]
    (let [id (get-in db [:form.adserver :value :id])
          event (condp = current-adserver-type
                  :adform :resource.adserver-account/adform-forget
                  :dcm :resource.adserver-account/dcm-forget
                  nil)]
      {:dispatch-n [(when id [event id])]})))

;; Initializes form to register the forwarded event
(rf/reg-event-fx :form.adserver/load-adserver
  (fn [_ [_ adserver-id]]
    {:dispatch-n [[:form.adserver/init-form {}]
                  [:resource.adserver-account/get-one adserver-id]]}))

(rf/reg-event-fx :form.adserver/get-one-success
  (fn [_ [_ [_ adserver]]]
    (let [adserver* (assoc adserver :adserver {:id (:adserverId adserver)})]
      {:dispatch [:ocp/open {:id :adserver/crud
                             :props {:adserver adserver*}}]})))

(rf/reg-sub :form.adserver/dcm-profile-options
  (fn [db]
    (map-profile-options (get-in db [:resource.adserver-account/dcm-verify :value :userProfiles]))))

(rf/reg-sub :form.adserver/submittable?
  (fn [db]
    (let [{:keys [value initial-value]} (:form.adserver db)
          submittable-values [:adserver-id :name :username :password :api-access]]
      (not= (select-keys value submittable-values)
            (select-keys initial-value submittable-values)))))

(rf/reg-sub :form.adserver/current-adserver-type
  :<- [:form.adserver/current-adserver]
  (fn [current-adserver]
    (adserver.q/adserver-type-from-id current-adserver)))

(rf/reg-sub :form.adserver/current-adserver
  (fn [db]
    (get-in db [:form.adserver :value :adserver-id])))

(rf/reg-sub :form.adserver/profile-id
  (fn [db]
    (get-in db [:form.adserver :value :profile-id])))

(defhook use-current-adserver-name []
  (let [{adservers :data} (use-query (adserver.q/adservers-query))
        current-adserver-id (use-subscribe [:form.adserver/current-adserver])]
    (->> adservers
         (filter #(= (:id %) current-adserver-id))
         first
         :name
         adserver.q/rename-adserver-to-new-name)))

(rf/reg-sub :form.adserver/current-adserver-raw-credentials?
  :<- [:form.adserver/current-adserver]
  (fn [current-adserver]
    (adserver-raw-credentials? current-adserver)))

(rf/reg-sub :form.adserver/api-access
  (fn [db]
    (get-in db [:form.adserver :value :api-access])))

(rf/reg-sub :form.adserver/has-oauth-access?
  (fn [db]
    (get-in db [:form.adserver :value :oauth-access])))

(rf/reg-sub :form.adserver/oauth-user-info
  (fn [db]
    (let [current-adserver-type @(rf/subscribe [:form.adserver/current-adserver-type])
          path (condp = current-adserver-type
                 :adform :resource.adserver-account/adform-verify
                 :dcm :resource.adserver-account/dcm-verify
                 nil)]
      (rename-keys (get-in db [path :value]) {:displayName :name}))))

(rf/reg-sub :form.adserver/mode
  (fn [db]
    (get-in db [:form.adserver :mode])))

(rf/reg-sub :form.adserver/backend-error
  (fn [db]
    (get-in db [:form.adserver :backend-error])))

(rf/reg-sub :form.adserver/redirected-adserver
  (fn []
    (let [query-params (url/query-string->map
                        (.getQuery (url/current-uri)))
          adserver-account-id (:adserver-account-id query-params)]
      adserver-account-id)))

(def use-bind-control (partial form/use-bind-control :form.adserver))
(def use-error-message (partial form/use-error-message :form.adserver))
